import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TRADES, tradeForSpec } from '@/lib/trades';

// Preprocess numbers: transforme NaN / '' en undefined pour éviter que Zod rejette silencieusement.
const optionalNumber = (schema) =>
  z.preprocess((v) => (v === '' || v === null || Number.isNaN(v) ? undefined : v), schema);

// Preprocess strings: transforme null / undefined en '' (Zod string() rejette null)
const optionalString = () =>
  z.preprocess((v) => (v == null ? '' : v), z.string().optional());

const sousTraitantSchema = z.object({
  nom: z.string().min(1, 'Nom requis'),
  specialite: z.string().min(1, 'Spécialité requise'),
  telephone: z.string().min(1, 'Téléphone requis'),
  email: z.preprocess((v) => (v == null ? '' : v), z.string().email('Email invalide').optional().or(z.literal(''))),
  zone: optionalString(),
  note_qualite: optionalNumber(z.number().min(0).max(5).optional()),
  prix_range: z.preprocess((v) => (v == null || v === '' ? undefined : v), z.enum(['€', '€€', '€€€', '€€€€']).optional()),
  tarif_journalier: optionalNumber(z.number().positive().optional()),
  siret: optionalString(),
  certifications: optionalString(),
  notes: optionalString(),
  specialites_secondaires: z.array(z.string()).optional(),
  forme_juridique: optionalString(),
  capital_social: optionalNumber(z.number().optional()),
  rcs_ville: optionalString(),
  numero_rcs: optionalString(),
  adresse: optionalString(),
  code_postal: optionalString(),
  ville: optionalString(),
});

export default function SousTraitantForm({ sousTraitant, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [siretLoading, setSiretLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(sousTraitantSchema),
    defaultValues: sousTraitant || { specialites_secondaires: [] },
  });

  const prixRangeValue = watch('prix_range');
  const siretValue = watch('siret');
  const specialiteValue = watch('specialite') || '';
  const specialitesSecondaires = watch('specialites_secondaires') || [];

  // Corps de métier « principal » de l'artisan — on l'exclut de la liste des
  // secondaires cochables pour éviter la redondance.
  const primaryTradeKey = tradeForSpec(specialiteValue).key;

  // Ensemble des corps déjà cochés (résolus depuis les valeurs stockées, qui
  // peuvent être des libellés libres pré-refonte ou des clés canoniques).
  const checkedTradeKeys = new Set(
    specialitesSecondaires.map((s) => tradeForSpec(s).key).filter((k) => k && k !== 'Autres'),
  );

  const handleSiretLookup = async () => {
    const siret = (siretValue || '').replace(/\s/g, '');
    if (!siret || siret.length < 9) {
      toast.error('Renseignez au moins un SIREN (9 chiffres) ou SIRET (14 chiffres)');
      return;
    }
    setSiretLoading(true);
    try {
      const r = await api.get(`/siret/lookup/${siret}`);
      const d = r.data || {};
      if (d.nom) setValue('nom', d.nom);
      if (d.forme_juridique) setValue('forme_juridique', d.forme_juridique);
      if (d.capital_social !== null && d.capital_social !== undefined) setValue('capital_social', d.capital_social);
      if (d.rcs_ville) setValue('rcs_ville', d.rcs_ville);
      if (d.numero_rcs) setValue('numero_rcs', d.numero_rcs);
      if (d.adresse) setValue('adresse', d.adresse);
      if (d.code_postal) setValue('code_postal', d.code_postal);
      if (d.ville) setValue('ville', d.ville);
      if (d.siret) setValue('siret', d.siret);
      toast.success(`Fiche mise à jour : ${d.nom || 'société trouvée'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aucune société trouvée pour ce SIRET');
    } finally {
      setSiretLoading(false);
    }
  };

  const toggleSecondaryTrade = (tradeKey) => {
    if (tradeKey === primaryTradeKey) return; // never allow toggling the primary
    // Normalise la liste actuelle en clés canoniques et enlève les doublons
    const normalised = Array.from(new Set(
      specialitesSecondaires
        .map((s) => tradeForSpec(s).key)
        .filter((k) => k && k !== 'Autres' && k !== primaryTradeKey),
    ));
    const isChecked = normalised.includes(tradeKey);
    const next = isChecked
      ? normalised.filter((s) => s !== tradeKey)
      : [...normalised, tradeKey];
    setValue('specialites_secondaires', next, { shouldDirty: true });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Nettoyer les NaN qui pourraient rester
      const payload = { ...data };
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === 'number' && Number.isNaN(payload[k])) payload[k] = undefined;
      });
      if (sousTraitant && sousTraitant.sous_traitant_id) {
        await api.put(`/sous-traitants/${sousTraitant.sous_traitant_id}`, payload);
        toast.success('Sous-traitant mis à jour');
        onSuccess();
      } else {
        const r = await api.post('/sous-traitants', payload);
        // Le message success final est dans onSuccess
        // Passe le sous-traitant créé (avec id + email) pour permettre la génération du lien onboarding
        onSuccess(r.data);
      }
    } catch (error) {
      console.error('Erreur sauvegarde sous-traitant:', error);
      toast.error(error?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const onError = (errs) => {
    const first = Object.values(errs)[0];
    toast.error(first?.message || 'Vérifiez les champs obligatoires');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-4">
      <div>
        <Label htmlFor="nom">Nom *</Label>
        <Input id="nom" {...register('nom')} data-testid="st-nom-input" />
        {errors.nom && <p className="text-sm text-red-600 mt-1">{errors.nom.message}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="specialite">Spécialité *</Label>
          <Input
            id="specialite"
            {...register('specialite')}
            placeholder="Ex: Plomberie, Électricité..."
            data-testid="st-specialite-input"
          />
          {errors.specialite && <p className="text-sm text-red-600 mt-1">{errors.specialite.message}</p>}
        </div>

        <div>
          <Label htmlFor="zone">Zone d'Intervention</Label>
          <Input id="zone" {...register('zone')} placeholder="Ex: Paris, Île-de-France..." data-testid="st-zone-input" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="telephone">Téléphone *</Label>
          <Input id="telephone" {...register('telephone')} data-testid="st-telephone-input" />
          {errors.telephone && <p className="text-sm text-red-600 mt-1">{errors.telephone.message}</p>}
        </div>

        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" {...register('email')} data-testid="st-email-input" />
          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="note_qualite">Note Qualité (0-5)</Label>
          <Input
            id="note_qualite"
            type="number"
            step="0.1"
            min="0"
            max="5"
            {...register('note_qualite', { valueAsNumber: true })}
            data-testid="st-note-input"
          />
          {errors.note_qualite && <p className="text-sm text-red-600 mt-1">{errors.note_qualite.message}</p>}
        </div>

        <div>
          <Label htmlFor="prix_range">Niveau de Prix</Label>
          <Select value={prixRangeValue} onValueChange={(value) => setValue('prix_range', value)}>
            <SelectTrigger data-testid="st-prix-select">
              <SelectValue placeholder="Sélectionner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="€">€ (Économique)</SelectItem>
              <SelectItem value="€€">€€ (Modéré)</SelectItem>
              <SelectItem value="€€€">€€€ (Élevé)</SelectItem>
              <SelectItem value="€€€€">€€€€ (Très Élevé)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="tarif_journalier">Tarif Journalier (€/jour)</Label>
          <Input
            id="tarif_journalier"
            type="number"
            step="0.01"
            {...register('tarif_journalier', { valueAsNumber: true })}
            placeholder="Ex: 450"
          />
        </div>

        <div>
          <Label htmlFor="siret">SIRET / SIREN</Label>
          <div className="flex gap-2">
            <Input
              id="siret"
              {...register('siret')}
              placeholder="14 ou 9 chiffres"
              maxLength="17"
              data-testid="st-siret-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleSiretLookup}
              disabled={siretLoading}
              className="shrink-0"
              data-testid="st-siret-search-btn"
              title="Récupérer les infos société via la base INSEE"
            >
              {siretLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Corps de métier supplémentaires (cases à cocher) */}
      <div>
        <Label>Autres corps de métier</Label>
        <p className="text-xs text-slate-500 mb-2">
          Cochez tous les corps de métier que cet artisan sait faire. Il apparaîtra automatiquement
          dans chaque groupe correspondant sur la page Sous-traitants.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto p-2 border border-slate-200 rounded-lg bg-slate-50/50">
          {TRADES.map((t) => {
            const Icon = t.icon;
            const isPrimary = t.key === primaryTradeKey;
            const isChecked = isPrimary || checkedTradeKeys.has(t.key);
            return (
              <label
                key={t.key}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs cursor-pointer transition-colors border ${
                  isPrimary
                    ? 'bg-blue-100 border-blue-300 text-blue-800 cursor-not-allowed'
                    : isChecked
                      ? 'bg-white border-blue-400 text-blue-800 shadow-sm'
                      : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50/50'
                }`}
                data-testid={`st-trade-${t.key}`}
                title={isPrimary ? 'Corps de métier principal — modifiable via le champ Spécialité' : undefined}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isPrimary}
                  onChange={() => toggleSecondaryTrade(t.key)}
                  className="w-3.5 h-3.5 shrink-0 accent-blue-600"
                />
                <Icon className={`w-3.5 h-3.5 shrink-0 ${t.text}`} />
                <span className="truncate">
                  {t.key}
                  {isPrimary && <span className="ml-1 text-[9px] font-semibold uppercase">Principal</span>}
                </span>
              </label>
            );
          })}
        </div>
        {checkedTradeKeys.size > 0 && (
          <p className="text-xs text-slate-500 mt-2">
            <b>{checkedTradeKeys.size}</b> corps supplémentaire(s) sélectionné(s)
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="certifications">Certifications / Qualifications</Label>
        <Input
          id="certifications"
          {...register('certifications')}
          placeholder="Ex: RGE, Qualibat, ..."
        />
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea id="notes" {...register('notes')} rows={4} data-testid="st-notes-input" />
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-btn">
          Annuler
        </Button>
        <Button type="submit" disabled={loading} data-testid="submit-st-btn">
          {loading ? 'Enregistrement...' : sousTraitant ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
}